import Nav from "@/components/Nav";
import api from "@/lib/api";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Check, Crown, Sparkles, Users, Minus, Star } from "lucide-react";
import { motion } from "framer-motion";
import { usePageMeta } from "@/lib/pageMeta";

const TIERS = [
  {
    id: "premium",
    quarterId: "premium_quarter",
    name: "Premium",
    subtitle: "For serious singles",
    monthly: 749, monthlyOriginal: 1499,
    quarterly: 1999, quarterlyOriginal: 3999,
    icon: Sparkles,
    highlights: [
      "3 curated matches every Monday",
      "Full Marriage Intelligence Report per match",
      "Kundali × AI Fusion (bundled)",
      "See Last Active on every match",
      "1 expert consultation every 15 days (30 min)",
      "Family-shareable PDF & public link",
    ],
  },
  {
    id: "vip",
    quarterId: "vip_quarter",
    name: "VIP",
    subtitle: "Priority everything",
    monthly: 1999, monthlyOriginal: 3999,
    quarterly: 4999, quarterlyOriginal: 9999,
    featured: true,
    icon: Crown,
    highlights: [
      "5 curated matches every Monday — with Priority placement",
      "'Priority Match' badge on your profile in others' matches",
      "Full interaction timeline on every match",
      "Weekly 30-min consultation with K Singh",
      "Family Circle — 4 members can view reports",
      "Everything in Premium",
    ],
  },
];

const BASIC_HIGHLIGHTS = [
  "1 curated match every Monday",
  "Full 18-question Marriage Intelligence Assessment",
  "Compatibility Snapshot for matches",
  "Marriage Readiness Score",
];

const COMPARISON = [
  { f: "Weekly curated matches", basic: "1", premium: "3", vip: "5 + priority" },
  { f: "'Priority Match' badge", basic: null, premium: null, vip: true },
  { f: "Marriage Intelligence Reports", basic: "Snapshot only", premium: "Unlimited", vip: "Unlimited" },
  { f: "Kundali × AI Fusion", basic: null, premium: true, vip: true },
  { f: "Match status tracking (Sent / Viewed / Interested)", basic: true, premium: true, vip: true },
  { f: "Last Active on matches", basic: null, premium: true, vip: true },
  { f: "Full interaction timeline", basic: null, premium: null, vip: true },
  { f: "Expert consultation with K Singh", basic: null, premium: "Every 15 days", vip: "Every week" },
  { f: "Family Circle (4 members)", basic: null, premium: null, vip: true },
  { f: "Family-shareable PDF reports", basic: null, premium: true, vip: true },
];

function Cell({ v }) {
  if (v === true) return <Check size={16} className="text-amber-700 mx-auto" />;
  if (!v) return <Minus size={14} className="text-stone-300 mx-auto" />;
  return <span className="text-xs text-[#1E1B4B] font-medium">{v}</span>;
}

function getCtaLabel({ isCurrent, isLoading, tierName }) {
  if (isCurrent) return "Current plan";
  if (isLoading) return "Processing…";
  return `Choose ${tierName}`;
}

export default function Subscription() {
  usePageMeta(
    "Plans & Pricing",
    "Basic (FREE for first 1,000 members), Premium ₹749/mo, and VIP ₹1,999/mo — Marriage Intelligence for every stage. Limited period launch offer."
  );
  const { user, refresh } = useAuth();
  const [loading, setLoading] = useState("");
  const [quarterly, setQuarterly] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/public/stats"); setStats(data); } catch { /* noop */ }
    })();
  }, []);

  const subscribe = async (tierKey) => {
    setLoading(tierKey);
    try {
      const { data } = await api.post("/subscribe", { tier: tierKey });
      await refresh();
      toast.success(`Welcome to Anubandh ${data.tier.toUpperCase()}! (MOCKED Razorpay)`);
    } catch { toast.error("Payment failed"); }
    finally { setLoading(""); }
  };

  const tierNow = user?.subscription_tier || "basic";
  const spotsLeft = stats?.basic_spots_remaining;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="text-center">
          <div className="overline">Plans</div>
          <h1 className="font-serif-display text-4xl lg:text-6xl text-[#1E1B4B] mt-3">Invest in the right decision.</h1>
          <p className="mt-4 text-stone-600 max-w-2xl mx-auto">
            Sharper than swipe apps. More transparent than legacy portals. One decision, done right.
          </p>
          {user && <div className="mt-3 text-sm text-stone-500">Current plan: <span className="font-medium text-[#1E1B4B]">{tierNow === "vip" ? "VIP" : tierNow.charAt(0).toUpperCase() + tierNow.slice(1)}</span></div>}
        </div>

        <div className="mt-8 flex items-center justify-center gap-3">
          <span className={`text-sm ${!quarterly ? "text-[#1E1B4B] font-medium" : "text-stone-500"}`}>Monthly</span>
          <button
            data-testid="billing-toggle"
            onClick={() => setQuarterly(!quarterly)}
            className={`relative w-14 h-7 rounded-full transition-colors ${quarterly ? "bg-amber-500" : "bg-stone-300"}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${quarterly ? "translate-x-7" : ""}`} />
          </button>
          <span className={`text-sm ${quarterly ? "text-[#1E1B4B] font-medium" : "text-stone-500"}`}>Quarterly <span className="text-amber-700">· Save more</span></span>
        </div>

        <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-stone-700 max-w-3xl mx-auto text-center">
          Payments are <b>MOCKED</b> via Razorpay in this build — clicking subscribe upgrades your account instantly for testing.
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Basic — FREE limited */}
          <div className="border border-stone-200 rounded-2xl bg-white p-7 flex flex-col" data-testid="plan-basic">
            <div className="overline">Basic</div>
            <div className="flex items-baseline gap-2 mt-3">
              <span className="text-stone-400 line-through text-lg">₹999</span>
              <span className="font-serif-display text-3xl text-[#1E1B4B]">FREE</span>
            </div>
            <p className="text-xs text-amber-800 font-medium mt-1">Limited to first 1,000 members. After that ₹499/month.</p>
            {typeof spotsLeft === "number" && (
              <div className="mt-3" data-testid="basic-spots-counter">
                <div className="flex justify-between text-xs text-stone-500">
                  <span>{stats.basic_members_claimed} members joined</span>
                  <span className="text-amber-700 font-semibold">{spotsLeft} spots left</span>
                </div>
                <div className="mt-1.5 h-1.5 rounded-full bg-stone-100 overflow-hidden">
                  <div className="h-full rounded-full bg-amber-500" style={{ width: `${(stats.basic_members_claimed / stats.basic_free_limit) * 100}%` }} />
                </div>
              </div>
            )}
            <ul className="mt-6 space-y-2 text-sm flex-1">
              {BASIC_HIGHLIGHTS.map((f) => <li key={f} className="flex gap-2"><Check size={16} className="text-amber-700 mt-0.5 shrink-0" />{f}</li>)}
            </ul>
            <button disabled className="btn-pill mt-6 w-full py-3 text-sm text-stone-400 border border-stone-200">
              {["basic", "free"].includes(tierNow) ? "Current plan" : "Included"}
            </button>
          </div>

          {TIERS.map((t) => {
            const price = quarterly ? t.quarterly : t.monthly;
            const original = quarterly ? t.quarterlyOriginal : t.monthlyOriginal;
            const period = quarterly ? "/ quarter" : "/ month";
            const paymentKey = quarterly ? t.quarterId : t.id;
            const isCurrent = tierNow === t.id;
            return (
              <motion.div
                key={t.id}
                data-testid={`plan-${t.id}`}
                initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className={`rounded-2xl p-7 border relative flex flex-col ${t.featured ? "border-amber-400" : "border-stone-200"}`}
                style={t.featured ? { background: "#1E1B4B", color: "white" } : { background: "white" }}
              >
                <div className={`absolute top-4 right-4 text-[10px] uppercase tracking-widest rounded-full px-2.5 py-0.5 inline-flex items-center gap-1 ${t.featured ? "text-amber-300 bg-amber-500/15 border border-amber-400/40" : "text-amber-800 bg-amber-50 border border-amber-300"}`}>
                  <Star size={9} /> Limited Period Offer
                </div>
                <t.icon size={20} className={t.featured ? "text-amber-300" : "text-amber-700"} />
                <div className={`overline mt-4 ${t.featured ? "text-amber-300" : ""}`}>{t.subtitle}</div>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className={`font-serif-display text-3xl ${t.featured ? "text-white" : "text-[#1E1B4B]"}`}>{t.name}</span>
                </div>
                <div className="mt-4 flex items-baseline gap-2 flex-wrap">
                  <span className={`line-through text-lg ${t.featured ? "text-stone-400" : "text-stone-400"}`}>₹{original.toLocaleString("en-IN")}</span>
                  <span className={`font-serif-display text-3xl ${t.featured ? "text-white" : "text-[#1E1B4B]"}`}>₹{price.toLocaleString("en-IN")}</span>
                  <span className={`text-sm ${t.featured ? "text-stone-300" : "text-stone-500"}`}>{period}</span>
                </div>
                <ul className="mt-6 space-y-2 text-sm flex-1">
                  {t.highlights.map((f) => (
                    <li key={f} className="flex gap-2">
                      <Check size={16} className={t.featured ? "text-amber-300 mt-0.5 shrink-0" : "text-amber-700 mt-0.5 shrink-0"} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`subscribe-${t.id}-btn`}
                  onClick={() => subscribe(paymentKey)}
                  disabled={loading === paymentKey || isCurrent}
                  className={`btn-pill mt-8 w-full py-3 text-sm ${t.featured ? "bg-amber-500 text-[#1E1B4B]" : "bg-[#1E1B4B] text-white"}`}
                  style={isCurrent ? { background: "#78716C" } : {}}
                >
                  {getCtaLabel({ isCurrent, isLoading: loading === paymentKey, tierName: t.name })}
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Comparison table */}
        <div className="mt-16">
          <h2 className="font-serif-display text-2xl lg:text-3xl text-[#1E1B4B] text-center">Compare plans, feel the difference.</h2>
          <div className="mt-8 overflow-x-auto rounded-2xl border border-stone-200 bg-white" data-testid="plan-comparison-table">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="text-left p-4 text-xs uppercase tracking-widest text-stone-500 font-medium">Feature</th>
                  <th className="p-4 text-xs uppercase tracking-widest text-stone-500 font-medium w-32">Basic</th>
                  <th className="p-4 text-xs uppercase tracking-widest text-amber-800 font-medium w-32 bg-amber-50/60">Premium</th>
                  <th className="p-4 text-xs uppercase tracking-widest font-medium w-32 text-white" style={{ background: "#1E1B4B" }}>VIP</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, i) => (
                  <tr key={row.f} className={i % 2 ? "bg-[#F9F8F6]" : ""}>
                    <td className="p-4 text-stone-700">{row.f}</td>
                    <td className="p-4 text-center"><Cell v={row.basic} /></td>
                    <td className="p-4 text-center bg-amber-50/40"><Cell v={row.premium} /></td>
                    <td className="p-4 text-center bg-indigo-50/40"><Cell v={row.vip} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Family plan strip */}
        <div className="mt-16 border border-stone-200 rounded-2xl bg-white p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
          <Users size={28} className="text-amber-700" />
          <div className="flex-1">
            <div className="overline">Family Plan · Available on VIP</div>
            <h3 className="font-serif-display text-2xl text-[#1E1B4B] mt-2">One decision. Four voices. One quiet home.</h3>
            <p className="mt-2 text-stone-600 text-sm">Add up to 4 family members (parents, elders, trusted mentors) to your Family Circle. They see what you allow — and nothing else. No competitor offers this.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
